import { Constants } from '../shared/Constants';

/**
 * ActionBuilder Class
 */
export class ActionBuilder {
    constructor() {
        this._actions = [];
        this._customActionCounter = 0;
        this._header = 'Actions';

        return new Proxy(this, {
            get: (target, prop) => {
                if (prop in target) return target[prop];
                if (typeof prop === 'string') {
                    return () => target.custom(prop);
                }
                return undefined;
            }
        });
    }

    /**
     * Sets the header label for the Actions column.
     * @param {string} label
     * @returns {this}
     */
    withHeader(label) {
        this._header = label;
        return this;
    }

    _getHeader() {
        return this._header;
    }

    view() {
        const act = { key: Constants.Actions.VIEW, title: 'View Details', icon: Constants.Icons.VIEW, className: 'btn-outline-info', callback: null, showIf: null, color: null, textColor: null };
        this._actions.push(act);
        return this._wrap(act);
    }

    edit() {
        const act = { key: Constants.Actions.EDIT, title: 'Edit Record', icon: Constants.Icons.EDIT, className: 'btn-outline-primary', callback: null, showIf: null, color: null, textColor: null };
        this._actions.push(act);
        return this._wrap(act);
    }

    delete() {
        const act = { key: Constants.Actions.DELETE, title: 'Delete Record', icon: Constants.Icons.DELETE, className: 'btn-outline-danger', callback: null, showIf: null, color: null, textColor: null };
        this._actions.push(act);
        return this._wrap(act);
    }
    download() {
      const act = { key: Constants.Actions.DOWNLOAD, title: "Download", icon: Constants.Icons.DOWNLOAD, className: "btn-outline-primary", callback: null, showIf: null, color: null, textColor: null };
      this._actions.push(act);
      return this._wrap(act);
    }
    upload() {
      const act = { key: Constants.Actions.UPLOAD, title: "Upload", icon: "bi-upload", className: "btn-outline-secondary", callback: null, showIf: null, color: null, textColor: null };
      this._actions.push(act);
      return this._wrap(act);
    }
    refresh() {
      const act = { key: Constants.Actions.REFRESH, title: "Refresh", icon: "bi-arrow-clockwise", className: "btn-outline-secondary", callback: null, showIf: null, color: null, textColor: null };
      this._actions.push(act);
      return this._wrap(act);
    }

    approve() {
        const act = {
            key: Constants.Actions.APPROVE,
            title: 'Approve',
            icon: Constants.Icons.APPROVE,
            className: 'btn-outline-success',
            color: 'success',
            callback: null,
            showIf: null,
            textColor: null
        };
        this._actions.push(act);
        return this._wrap(act);
    }

    reject() {
        const act = {
            key: Constants.Actions.REJECT,
            title: 'Reject',
            icon: Constants.Icons.REJECT,
            className: 'btn-outline-danger',
            color: 'danger',
            callback: null,
            showIf: null,
            textColor: null
        };
        this._actions.push(act);
        return this._wrap(act);
    }

    envelope() {
        const act = {
            key: Constants.Actions.ENVELOPE,
            title: 'Send Email',
            icon: Constants.Icons.ENVELOPE,
            className: 'btn-outline-primary',
            callback: null,
            showIf: null,
            color: null,
            textColor: null
        };
        this._actions.push(act);
        return this._wrap(act);
    }

    button(key) {
        if (key && typeof this[key] === 'function' && key !== 'button' && key !== 'custom') {
            return this[key]();
        }
        return this.custom(key);
    }

    custom(key) {
        const standardKeys = Object.values(Constants.Actions);
        const normalizedKey = this._normalizeKey(key, 'custom-action');
        if (key && standardKeys.includes(String(key).toLowerCase())) {
            console.warn(`ActionBuilder: Use the dedicated .${String(key).toLowerCase()}() method instead of .custom('${key}') for standard actions.`);
        }
        const act = {
            key: normalizedKey,
            title: key || 'Custom Action',
            icon: null,
            className: 'btn-outline-secondary',
            callback: null,
            showIf: null,
            color: null,
            textColor: null
        };
        this._actions.push(act);
        return this._wrap(act);
    }

    _normalizeKey(key, prefix) {
        if (typeof key === 'string') {
            const normalized = key.trim().toLowerCase().replace(/\s+/g, '-');
            if (normalized) return normalized;
        }

        this._customActionCounter += 1;
        return `${prefix}-${this._customActionCounter}`;
    }

    _wrap(act) {
        return {
            withTitle: (t) => { act.title = t; return this._wrap(act); },
            withIcon: (i) => {
                const validIcons = Object.values(Constants.Icons);
                if (!i.startsWith('bi-') && !validIcons.includes(i)) {
                    console.warn(`ActionBuilder: "${i}" may not be a valid Bootstrap Icon.`);
                }
                act.icon = i;
                return this._wrap(act);
            },
            withColor: (c) => {
                const validThemes = Object.values(Constants.Theme);
                if (!validThemes.includes(c) && !c.startsWith('#') && !c.startsWith('rgb')) {
                    console.warn(`ActionBuilder: "${c}" is not a standard Theme constant or hex color.`);
                }
                act.color = c;
                return this._wrap(act);
            },
            withBackgroundColor: (c) => {
                const validThemes = Object.values(Constants.Theme);
                if (!validThemes.includes(c) && !c.startsWith('#') && !c.startsWith('rgb')) {
                    console.warn(`ActionBuilder: "${c}" is not a standard Theme constant or hex color.`);
                }
                act.color = c;
                return this._wrap(act);
            },
            withTextColor: (c) => {
                act.textColor = c;
                return this._wrap(act);
            },
            withClass: (c) => { act.className = c; return this._wrap(act); },
            showIf: (conditionFn) => { act.showIf = conditionFn; return this._wrap(act); },
            onClick: (cb) => { act.callback = cb; return this._wrap(act); },
            onProcess: (cb) => { act.callback = cb; return this._wrap(act); }
        };
    }

    _getActions() {
        return this._actions;
    }
}
